<template>
  <div>
    <div
      v-show="!showHtml"
      :id="containerId"
      :ref="containerId"
      class="quill-container"
      :style="{ height: height }"
    />
    <v-textarea
      v-show="showHtml"
      v-model="htmlValue"
      outlined
      class="subtitle-1 font-weight-regular"
      auto-grow
      hide-details
      @change="$emit('input', htmlValue)"
    />
    <v-menu
      v-model="showLinkSearch"
      :position-x="x"
      :position-y="y"
      absolute
      offset-y
      content-class="white overflow-hidden"
      :close-on-content-click="false"
    >
      <v-row dense>
        <v-col cols="auto">
          <v-select
            v-model="linkType"
            label="Type"
            :items="['Search', 'Manual']"
            class="min-width ma-2 full-height"
            hide-details
          />
        </v-col>
        <v-col>
          <v-autocomplete
            v-if="linkType == 'Search'"
            v-model="selectedPage"
            autofocus
            hide-details
            class="ma-2"
            label="Search"
            :items="pages"
            item-text="title"
            item-value="fullUrl"
            :filter="customFilter"
            @change="insertLink"
          >
            <template #[`item`]="data">
              <v-list-item-content>
                <v-list-item-title>{{ data.item.title }}</v-list-item-title>
                <v-list-item-subtitle>{{ data.item.url }}</v-list-item-subtitle>
              </v-list-item-content>
            </template>
          </v-autocomplete>
          <v-text-field
            v-else
            v-model="selectedPage"
            class="ma-2"
            hide-details
            autofocus
            label="Manual"
          >
            <template #append>
              <v-btn
                icon
                :disabled="!selectedPage"
                color="success"
                small
              >
                <v-icon @click="insertLink"> check </v-icon>
              </v-btn>
            </template>
          </v-text-field>
        </v-col>
      </v-row>
    </v-menu>
    <SnippetDialog
      v-if="enableSnippet"
      @save="insertSnippet"
    />
  </div>
</template>

<script>
import SnippetDialog from '@/components/content/SnippetDialog';
import Quill from 'quill';
import { mapGetters } from 'vuex';
export default {
  name: 'QuillEditor',
  components: { SnippetDialog },
  props: {
    containerId: { type: String },
    value: { type: String },
    height: { type: String, default: '100%' },
    id: { type: String },
    enableSnippet: { type: Boolean, default: false }
  },
  data() {
    return {
      editor: null,
      showLinkSearch: false,
      x: 0,
      y: 0,
      selectedPage: null,
      selectedRange: null,
      htmlValue: null,
      showHtml: false,
      cursor: null,
      linkType: 'Search'
    };
  },
  computed: {
    ...mapGetters({
      pages: 'lookup/pages',
      topic_name: 'lookup/topic_name',
      category_name: 'lookup/category_name',
      industry_name: 'lookup/industry_name',
      design_group_name: 'lookup/design_group_name'
    })
  },
  watch: {
    id(val, oldVal) {
      if (val !== oldVal) {
        this.setEditor();
      }
    },
    value(val, oldVal) {
      if (val !== oldVal && this.editor && !oldVal) {
        this.setEditor();
      }
    }
  },
  mounted() {
    this.setup();
  },
  methods: {
    customFilter(item, queryText, itemText) {
      const textOne = item.title.toLowerCase();
      const textTwo = item.url.toLowerCase();
      const searchText = queryText.toLowerCase();

      return textOne.indexOf(searchText) > -1 || textTwo.indexOf(searchText) > -1;
    },
    toggleHtml() {
      if (this.showHtml) {
        this.$emit('input', this.htmlValue);
        this.setEditor(this.htmlValue);
      } else {
        this.htmlValue = this.editor.root.innerHTML;
      }
      this.showHtml = !this.showHtml;
    },
    setup() {
      if (this.editor) {
        return;
      }
      const self = this;
      var toolbarOptions = [
        [{ header: [2, 3, false] }],
        ['bold', 'italic', 'underline'],
        [{ list: 'ordered' }, { list: 'bullet' }],
        [{ align: [] }],
        ['link', 'image', 'blockquote']
      ];
      if (this.enableSnippet) {
        toolbarOptions.push(['snippet']);
      }
      toolbarOptions.push(['html']);
      var options = {
        modules: {
          toolbar: toolbarOptions,
          counter: {
            unit: 'word'
          }
        },
        placeholder: 'Compose an epic...',
        theme: 'snow'
      };

      var Parchment = Quill.import('parchment');
      var icons = Quill.import('ui/icons');

      const htmlClass = new Parchment.Attributor.Class('html', 'ql-html', {
        scope: Parchment.Scope.INLINE
      });
      Quill.register(htmlClass, true);
      icons.html = '<i aria-hidden="true" class="v-icon material-icons">html</i>';

      var Link = Quill.import('formats/link');
      class MyLink extends Link {
        static create(value) {
          const node = super.create(value);
          value = this.sanitize(value);
          node.setAttribute('href', value);
          node.removeAttribute('target');
          node.removeAttribute('rel');
          return node;
        }
      }
      Quill.register(MyLink);

      if (this.enableSnippet) {
        const BlockEmbed = Quill.import('blots/embed');
        BlockEmbed.whitelist = ['mcs-snippet', 'span'];

        class SnippetBlot extends BlockEmbed {
          static create(value) {
            const node = super.create();
            let html = 'Snippet';
            node.setAttribute('contenteditable', false);
            if (value.templateId) {
              node.setAttribute('data-template-id', value.templateId);
              html += ` [Template Id: ${value.templateId}]`;
            }
            if (value.categoryId) {
              node.setAttribute('data-category-id', value.categoryId);
              html += ` [Category: ${self.category_name(value.categoryId)} (${value.categoryId})]`;
            }
            if (value.industryId) {
              node.setAttribute('data-industry-id', value.industryId);
              html += ` [Industry: ${self.industry_name(value.industryId)} (${value.industryId})]`;
            }
            if (value.topicId) {
              node.setAttribute('data-topic-id', value.topicId);
              html += ` [Topic: ${self.topic_name(value.topicId)} (${value.topicId})]`;
            }
            if (value.designGroupId) {
              node.setAttribute('data-design-group-id', value.designGroupId);
              html += ` [Design Group: ${self.design_group_name(value.designGroupId)} (${
                value.designGroupId
              })]`;
            }
            if (value.type) {
              node.setAttribute('data-type', value.type);
              if (value.type === 'direct_mail') {
                html += ' [Direct Mail]';
              }
            }
            node.innerHTML = html;
            return node;
          }

          static value(node) {
            return {
              templateId: node.getAttribute('data-template-id'),
              categoryId: node.getAttribute('data-category-id'),
              industryId: node.getAttribute('data-industry-id'),
              topicId: node.getAttribute('data-topic-id'),
              designGroupId: node.getAttribute('data-design-group-id'),
              type: node.getAttribute('data-type')
            };
          }
        }
        SnippetBlot.blotName = 'snippet';
        SnippetBlot.tagName = 'mcs-snippet';
        Quill.register(SnippetBlot, true);
        icons.snippet = '<i aria-hidden="true" class="v-icon material-icons">dashboard</i>';
      }

      class Counter {
        constructor(quill, options) {
          this.quill = quill;
          this.options = options;
          quill.on('text-change', this.update.bind(this));

          setTimeout(() => {
            this.update(); // Account for initial contents
          }, 1000);
        }

        calculate() {
          const text = this.quill.getText().trim();
          // Splitting empty text returns a non-empty array
          return text.length > 0 ? text.split(/\s+/).length : 0;
        }

        update() {
          var length = this.calculate();
          self.$emit('wordcount', length);
        }
      }

      Quill.register('modules/counter', Counter);

      this.editor = new Quill(this.$refs[this.containerId], options);

      this.editor.on('editor-change', function () {
        const range = self.editor.getSelection();
        if (range) {
          self.cursor = range.index;
        }
      });

      this.editor.on('text-change', function () {
        const range = self.editor.getSelection();
        if (range) {
          self.cursor = range.index;
        }
        self.$emit('input', self.editor.root.innerHTML);
      });

      this.editor.on('selection-change', function (range) {
        if (range) {
          if (range.length > 0) {
            self.selectedRange = range;
          }
          self.cursor = range.index;
        }
      });

      var toolbar = this.editor.getModule('toolbar');
      toolbar.addHandler('html', function () {
        self.toggleHtml();
      });
      toolbar.addHandler('snippet', function () {
        self.showSnippet();
      });
      toolbar.addHandler('link', function () {
        const link = this.controls[7][1].getBoundingClientRect();
        self.x = link.left;
        self.y = link.top;
        self.showLinkSearch = true;
      });
      toolbar.addHandler('image', function () {
        let fileInput = this.container.querySelector('input.ql-image[type=file]');
        if (fileInput == null) {
          fileInput = document.createElement('input');
          fileInput.setAttribute('type', 'file');
          fileInput.setAttribute(
            'accept',
            'image/png, image/gif, image/jpeg, image/bmp, image/x-icon'
          );
          fileInput.classList.add('ql-image');
          fileInput.addEventListener('change', () => {
            if (fileInput.files != null && fileInput.files[0] != null) {
              self.$store
                .dispatch('content/convertImage', {
                  file: fileInput.files[0]
                })
                .then((x) => {
                  var range = self.editor.getSelection();
                  self.editor.insertEmbed(range.index, 'image', x, Quill.sources.USER);
                });
            }
          });
          this.container.appendChild(fileInput);
        }
        fileInput.click();
      });

      if (this.value) {
        this.setEditor();
      }
    },
    setEditor(override) {
      const delta = this.editor.clipboard.convert(`<div>${override || this.value}</div>`);

      this.editor.setContents(delta, 'silent');
    },
    insertLink() {
      this.editor.setSelection(this.selectedRange.index, this.selectedRange.length);
      this.editor.theme.tooltip.edit('link', this.selectedPage);
      this.editor.theme.tooltip.save();

      this.selectedRange = null;
      this.showLinkSearch = false;
      this.$nextTick(() => {
        this.selectedPage = null;
      });
    },
    showSnippet() {
      const data = {};
      this.$bus.$emit(this.$enums.EVENT_BUS_EVENTS.EDIT_SNIPPET_SHOW, data);
    },
    insertSnippet(snippet) {
      this.editor.insertEmbed(this.cursor, 'snippet', snippet, Quill.sources.USER);
    }
  }
};
</script>

<style lang="scss" scoped>
.quill-container {
  height: 100%;

  ::v-deep a {
    color: var(--v-info-base) !important;
  }
}
</style>
